// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { printItems } from "@bg/App/Utilities/PrintItemsQrCode";
import QrCode from "@bg/XDL/Icons/QrCode";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useEffect, useState } from "react";
import { QRCode } from "react-qrcode-logo";
import { connect } from "react-redux";
import { css } from "styled-components";

type OwnProps = Partial<WidgetProps> & HasProductContext & Partial<ReturnType<typeof mapStateToProps>>;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

export interface ProductDetailsQrCodeeStyles {
    wrapper?: InjectableCss;
    qrCodeButton?: ButtonPresentationProps;
}

export const qrCodeButtonStyles: ProductDetailsQrCodeeStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            @media (max-width: 481px) {
                justify-content: flex-start;
            }
        `,
    },
    qrCodeButton: {
        css: css`
            display: flex;
            align-items: center;
            padding: 0 12px;
            white-space: nowrap;
            ${IconWrapper} {
                margin-right: 5px;
            }
            &&:hover {
                ${IconWrapper} svg {
                    color: ${getColor("common.white")};
                }
            }
        `,
    },
};

const styles = qrCodeButtonStyles;

const ProductDetailsQrCode: React.FC<OwnProps> = ({ productContext }) => {
    const { product, productInfo } = productContext;
    const [pdpFullpath, setPdpFullpath] = useState("");

    if (!product || !productInfo) {
        return null;
    }

    useEffect(() => {
        if (product && productInfo && window) {
            setPdpFullpath(`${window.location.origin}${product.canonicalUrl}`);
        }
    }, [productContext]);

    // print line qr code
    const printItem = () => {
        printItems(`#product_${product.id} canvas`, product, [productInfo], true, true);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Button sizeVariant="small" onClick={printItem} {...styles.qrCodeButton}>
                <Icon src={QrCode} size={18} />
                <Typography lineHeight="19px">{translate("Print Qr Code")}</Typography>
            </Button>
            <iframe
                id="qrCodePDP"
                style={{ position: "absolute", height: "0", width: "0", opacity: 0 }}
                name="qrCodePDP"
            />
            <StyledWrapper
                css={css`
                    display: none;
                `}
                id={`product_${product.id}`}
            >
                <QRCode value={pdpFullpath} logoWidth={250} logoHeight={250} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export const ProductDetailsQrCodeComponent: React.FC<OwnProps> = ProductDetailsQrCode;

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProductContext(ProductDetailsQrCode)),
    definition: {
        displayName: "QR Code",
        group: "XNGAGE" as any,
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
